import { useCallback, useEffect, useMemo, useState } from 'react';
import type { HeroImage } from './types';
import {
  galleryFingerprint,
  heroImagesToPublic,
  mapPublicImagesToHero,
  type PublicGalleryImage,
} from './vehicleGallerySync';

/**
 * Owner gallery CRUD used to live in a parallel `heroImages` list. Any
 * `setVehicleData` (mods reload, settings, etc.) rebuilt galleryImages from
 * stale `vehicleData.images` and overwrote the local list until a full refresh.
 * Sync only when the image identity/order/title actually changes.
 */
export function useSyncedHeroGallery({
  images,
  label,
  getFileUrl,
  onImagesCommit,
}: {
  images: PublicGalleryImage[] | undefined;
  label: string;
  getFileUrl: (path: string) => string | null;
  onImagesCommit: (images: PublicGalleryImage[]) => void;
}): {
  galleryImages: HeroImage[];
  heroImages: HeroImage[];
  commitGallery: (next: HeroImage[]) => void;
} {
  const fingerprint = useMemo(() => galleryFingerprint(images), [images]);
  const galleryImages = useMemo(
    () => mapPublicImagesToHero(images, label, getFileUrl),
    [getFileUrl, images, label]
  );
  const [heroImages, setHeroImages] = useState<HeroImage[]>([]);

  useEffect(() => {
    setHeroImages(galleryImages);
  }, [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps -- gallery identity only

  const commitGallery = useCallback(
    (next: HeroImage[]) => {
      setHeroImages(next);
      onImagesCommit(heroImagesToPublic(next));
    },
    [onImagesCommit]
  );

  return { galleryImages, heroImages, commitGallery };
}
